import {
  ByteQueue,
  buildFragment,
  buildHeader,
  buildTransferUrl,
  createEncryptStream,
  deriveFileKey,
  deriveManifestKey,
  encryptFileMeta,
  encryptManifest,
  encryptedSize,
  frameOffset,
  fromBase64Url,
  generateMasterKey,
  generatePasswordSalt,
  generateToken,
  hashToken,
  parseFragment,
  parseHeader,
  toBase64Url,
  utf8,
  wrapMasterKey,
  type Fragment,
  type Header,
  type Manifest,
} from "@limeshare/lsc1";
import { PART_URL_BATCH, TIERS, partCountFor, planPartSize } from "@limeshare/shared";
import { api } from "../api";
import { PUBLIC_ORIGIN, UPLOAD_CONCURRENCY } from "../config";
import { addSent } from "../history";
import { argon2idKdf } from "./kdf";
import {
  deleteResume,
  putResume,
  unwrapWithDeviceKey,
  wrapWithDeviceKey,
  type ResumeRecord,
} from "./resume-store";

export type UploadPhase = "preparing" | "uploading" | "finalizing" | "done";

export interface UploadProgress {
  phase: UploadPhase;
  fileIndex: number;
  fileName: string;
  /** encrypted bytes confirmed uploaded (all files) */
  bytesDone: number;
  /** total encrypted bytes (all files) */
  bytesTotal: number;
  /** plaintext bytes encrypted so far for the current file */
  encryptedOfCurrent: number;
}

export interface UploadOptions {
  files: File[];
  burn: boolean;
  /** requested lifetime in seconds; server clamps to the tier maximum */
  retentionSeconds?: number;
  /** optional note, encrypted into the manifest */
  message?: string;
  /** optional Studio / Brand Name, encrypted into the manifest as Verified Delivery badge */
  sender?: string;
  /** optional Pay-to-Decrypt price in USD */
  paywallPriceUsd?: number;
  /** optional password → Argon2id-wrapped key in the link */
  password?: string;
  /** optional NOWPayments Pass Code / Exceed Boost code */
  boostCode?: string;
  turnstileToken?: string;
  onProgress: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

export interface UploadResult {
  transferId: string;
  token: string;
  link: string;
  expiresAt: number | null;
  protected: boolean;
}

export class UploadError extends Error {
  readonly reason: unknown;
  constructor(message: string, reason?: unknown) {
    super(message);
    this.name = "UploadError";
    this.reason = reason;
  }
}

/** Client-side pre-checks mirroring the guest tier (or Boost/Pass tier) so users get instant feedback. */
export function validateSelection(files: File[], hasBoost = false): string | null {
  const t = TIERS.guest;
  const maxFiles = hasBoost ? 2500 : t.maxFiles;
  const maxBytes = hasBoost ? 1024 * 1024 ** 3 : t.maxTransferBytes;
  if (files.length === 0) return "Select at least one file.";
  if (files.length > maxFiles) return `Up to ${maxFiles} files per transfer.`;
  const tooBig = files.find((f) => f.size > maxBytes);
  if (tooBig) return `"${tooBig.name}" exceeds ${Math.round(maxBytes / 1024 ** 3)} GB limit. Use Instant Exceed Boost for up to 1 TB.`;
  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > maxBytes) return `Transfer exceeds ${Math.round(maxBytes / 1024 ** 3)} GB free limit. Use Instant Exceed Boost for up to 1 TB.`;
  return null;
}

/* ------------------------------------------------------------------ */
/* Fresh upload (§8) */

export async function uploadTransfer(o: UploadOptions): Promise<UploadResult> {
  const { files, signal } = o;
  const problem = validateSelection(files, Boolean(o.boostCode));
  if (problem) throw new UploadError(problem);

  const plans = files.map((file) => {
    const E = encryptedSize(file.size);
    const partSize = planPartSize(E);
    return { file, E, partSize, partCount: partCountFor(E, partSize) };
  });
  const bytesTotal = plans.reduce((s, p) => s + p.E, 0);
  const progress = makeReporter(o.onProgress, plans.map((p) => p.file.name), bytesTotal);
  progress.report("preparing", 0, 0);
  throwIfAborted(signal);

  // Token is generated here; the server only ever receives its hash.
  const token = generateToken();
  const tokenHash = await hashToken(token);

  const init = await api.initTransfer({
    files: plans.map((p) => ({ encryptedSize: p.E, partSize: p.partSize })),
    tokenHash,
    burn: o.burn,
    ...(o.retentionSeconds ? { retentionSeconds: o.retentionSeconds } : {}),
    ...(o.boostCode?.trim() ? { boostCode: o.boostCode.trim() } : {}),
    ...(o.paywallPriceUsd && o.paywallPriceUsd >= 2 ? { paywallPriceUsd: o.paywallPriceUsd } : {}),
    ...(o.turnstileToken ? { turnstileToken: o.turnstileToken } : {}),
  });
  if (init.files.length !== plans.length) throw new UploadError("Server returned an unexpected file list.");
  const { transferId } = init;

  // Keys exist only now: HKDF is salted with the server-assigned transferId.
  const masterKey = generateMasterKey();

  // The share fragment is fixed up-front (password wrapping included) so a resumed upload yields the same link.
  let fragment: Fragment = { kind: "plain", key: masterKey, token };
  if (o.password) {
    const salt = generatePasswordSalt();
    const kek = await argon2idKdf(o.password, salt);
    fragment = { kind: "password", kdf: "argon2id", wrappedKey: await wrapMasterKey(masterKey, kek, transferId), salt, token };
  }

  // Resume record (§12): headers are fixed now so the container is reproducible byte-for-byte later.
  const record: ResumeRecord = {
    transferId,
    createdAt: Date.now(),
    draftExpiresAt: init.draftExpiresAt,
    masterKeyWrapped: await wrapWithDeviceKey(masterKey),
    tokenWrapped: await wrapWithDeviceKey(utf8(token)),
    fragment: buildFragment(fragment),
    burn: o.burn,
    protected: Boolean(o.password),
    message: o.message?.trim() ? o.message.trim().slice(0, 2000) : undefined,
    sender: o.sender?.trim() ? o.sender.trim().slice(0, 64) : undefined,
    files: plans.map((p, i) => {
      const sf = init.files[i]!;
      if (sf.partSize !== p.partSize || sf.partCount !== p.partCount) throw new UploadError("Server part plan differs from the client plan.");
      const header = buildHeader({ plaintextLength: p.file.size });
      return {
        fileId: sf.fileId,
        name: p.file.name,
        size: p.file.size,
        type: p.file.type || "",
        lastModified: p.file.lastModified,
        header: toBase64Url(header),
        encryptedSize: p.E,
        partSize: p.partSize,
        partCount: p.partCount,
        parts: {},
      };
    }),
  };
  await putResume(record).catch(() => undefined);

  const initialUrls = new Map(init.files.map((f) => [f.fileId, new Map(f.partUrls.map((u) => [u.partNumber, u.url]))]));
  return runTransfer(record, masterKey, token, files, initialUrls, progress, signal);
}

/* ------------------------------------------------------------------ */
/* Resume (§12) */

export interface ResumeMatch {
  record: ResumeRecord;
  /** files re-selected by the user, matched to record.files by name/size/lastModified */
  files: (File | null)[];
}

export function matchFilesForResume(record: ResumeRecord, candidates: File[]): ResumeMatch {
  const files = record.files.map(
    (rf) => candidates.find((f) => f.name === rf.name && f.size === rf.size && f.lastModified === rf.lastModified) ?? null,
  );
  return { record, files };
}

export async function resumeTransfer(
  match: ResumeMatch,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  const { record } = match;
  if (match.files.some((f) => f === null)) throw new UploadError("Select the same files again to resume this upload.");
  if (record.draftExpiresAt <= Date.now()) {
    await deleteResume(record.transferId).catch(() => undefined);
    throw new UploadError("The upload window for this transfer has closed. Start a new transfer.");
  }
  const masterKey = await unwrapWithDeviceKey(record.masterKeyWrapped);
  const token = new TextDecoder().decode(await unwrapWithDeviceKey(record.tokenWrapped));
  const bytesTotal = record.files.reduce((s, f) => s + f.encryptedSize, 0);
  const progress = makeReporter(onProgress, record.files.map((f) => f.name), bytesTotal);
  progress.add(record.files.reduce((s, f) => s + Math.min(f.encryptedSize, Object.keys(f.parts).length * f.partSize), 0));
  return runTransfer(record, masterKey, token, match.files as File[], new Map(), progress, signal);
}

export async function discardResume(record: ResumeRecord): Promise<void> {
  try {
    const token = new TextDecoder().decode(await unwrapWithDeviceKey(record.tokenWrapped));
    await api.remove(record.transferId, token);
  } catch {
    /* draft will expire server-side anyway */
  }
  await deleteResume(record.transferId).catch(() => undefined);
}

/* ------------------------------------------------------------------ */
/* Shared pipeline */

interface Reporter {
  report: (phase: UploadPhase, fileIndex: number, encryptedOfCurrent: number) => void;
  add: (bytes: number) => void;
}

function makeReporter(onProgress: (p: UploadProgress) => void, names: string[], bytesTotal: number): Reporter {
  let bytesDone = 0;
  return {
    add: (b) => {
      bytesDone += b;
    },
    report: (phase, fileIndex, encryptedOfCurrent) =>
      onProgress({ phase, fileIndex, fileName: names[fileIndex] ?? "", bytesDone, bytesTotal, encryptedOfCurrent }),
  };
}

async function runTransfer(
  record: ResumeRecord,
  masterKey: Uint8Array,
  token: string,
  files: File[],
  initialUrls: Map<string, Map<number, string>>,
  progress: Reporter,
  signal: AbortSignal | undefined,
): Promise<UploadResult> {
  const { transferId } = record;
  const manifestKey = await deriveManifestKey(masterKey, transferId);
  const manifest: Manifest = {
    v: 1,
    files: [],
    created: Date.now(),
    ...(record.message ? { message: record.message } : {}),
    ...(record.sender ? { sender: record.sender } : {}),
  };
  const completedFiles: { fileId: string; encryptedFileMeta: string; parts: { partNumber: number; etag: string }[] }[] = [];

  for (let i = 0; i < record.files.length; i++) {
    const rf = record.files[i]!;
    const file = files[i]!;
    const fileKey = await deriveFileKey(masterKey, transferId, rf.fileId);
    const header = fromBase64Url(rf.header);
    const h = parseHeader(header);
    const C = 2 ** h.chunkLog2;
    // Resume point: first part without an ETag → first frame overlapping that container offset.
    const have = new Map(Object.entries(rf.parts).map(([n, etag]) => [Number(n), etag]));
    let firstMissing = 1;
    while (firstMissing <= rf.partCount && have.has(firstMissing)) firstMissing++;

    if (firstMissing <= rf.partCount) {
      const { startFrame, skipBytes: skip, containerStart } = computeResumePoint(h, rf.partSize, firstMissing);
      void containerStart;
      const enc = createEncryptStream({
        key: fileKey,
        plaintextLength: rf.size,
        chunkLog2: h.chunkLog2,
        noncePrefix: h.noncePrefix,
        startFrameIndex: startFrame,
        onProgress: (done) => progress.report("uploading", i, done),
      });
      if (enc.encryptedSize !== rf.encryptedSize) throw new UploadError("Container size mismatch.");

      const urls = initialUrls.get(rf.fileId) ?? new Map<number, string>();
      const urlFor = async (n: number, forceRefresh = false): Promise<string> => {
        if (forceRefresh || !urls.has(n)) {
          const more = await api.partUrls(transferId, rf.fileId, n, PART_URL_BATCH, token);
          for (const u of more.partUrls) urls.set(u.partNumber, u.url);
        }
        const url = urls.get(n);
        if (!url) throw new UploadError(`No upload URL for part ${n}`);
        return url;
      };

      const source = file.slice(startFrame * C).stream().pipeThrough(enc.stream);
      await uploadParts({
        source,
        skipBytes: skip,
        firstPart: firstMissing,
        partSize: rf.partSize,
        partCount: rf.partCount,
        urlFor,
        concurrency: UPLOAD_CONCURRENCY,
        signal,
        onPartDone: (partNumber, etag, bytes) => {
          have.set(partNumber, etag);
          rf.parts[String(partNumber)] = etag;
          progress.add(bytes);
          progress.report("uploading", i, rf.size);
          void putResume(record).catch(() => undefined);
        },
      });
    }

    if (have.size !== rf.partCount) throw new UploadError(`Uploaded ${have.size} parts, expected ${rf.partCount}.`);
    const meta = { name: rf.name, size: rf.size, type: rf.type, ...(rf.lastModified ? { mtime: rf.lastModified } : {}) };
    manifest.files.push({ id: rf.fileId, ...meta });
    completedFiles.push({
      fileId: rf.fileId,
      encryptedFileMeta: await encryptFileMeta(fileKey, transferId, rf.fileId, meta),
      parts: [...have.entries()].map(([partNumber, etag]) => ({ partNumber, etag })).sort((a, b) => a.partNumber - b.partNumber),
    });
  }

  const last = record.files.length - 1;
  progress.report("finalizing", last, record.files[last]!.size);
  const encryptedManifest = await encryptManifest(manifestKey, transferId, manifest);
  const done = await api.complete(transferId, token, { encryptedManifest, files: completedFiles });
  await deleteResume(transferId).catch(() => undefined);

  const fragment = parseFragment(record.fragment);
  const link = buildTransferUrl(PUBLIC_ORIGIN, transferId, fragment);
  addSent({
    transferId,
    token,
    link,
    createdAt: Date.now(),
    expiresAt: done.expiresAt,
    burn: record.burn,
    protected: record.protected,
    files: record.files.map((f) => ({ name: f.name, size: f.size })),
  });

  progress.report("done", last, record.files[last]!.size);
  return { transferId, token, expiresAt: done.expiresAt, link, protected: record.protected };
}

/**
 * �12 � where to restart encryption for the first part without an ETag.
 * Returns the frame whose bytes contain the part boundary and how many container bytes of that frame
 * belong to already-uploaded parts (to be dropped before cutting new parts).
 */
export function computeResumePoint(
  h: Header,
  partSize: number,
  firstMissingPart: number,
): { startFrame: number; skipBytes: number; containerStart: number } {
  const C = 2 ** h.chunkLog2;
  const n = Math.max(1, Math.ceil(h.plaintextLength / C));
  const containerStart = (firstMissingPart - 1) * partSize;
  if (containerStart <= 32) return { startFrame: 0, skipBytes: containerStart, containerStart };
  const startFrame = Math.min(Math.floor((containerStart - 32) / (C + 16)), n - 1);
  return { startFrame, skipBytes: containerStart - frameOffset(h, startFrame), containerStart };
}

export interface UploadPartsOptions {
  source: ReadableStream<Uint8Array>;
  /** container bytes at the start of `source` that belong to an already-uploaded part */
  skipBytes: number;
  firstPart: number;
  partSize: number;
  partCount: number;
  urlFor: (partNumber: number, forceRefresh?: boolean) => Promise<string>;
  concurrency: number;
  signal: AbortSignal | undefined;
  onPartDone: (partNumber: number, etag: string, bytes: number) => void;
}

/** Cuts the encrypted byte stream into uniform parts and PUTs them with bounded concurrency. */
export async function uploadParts(o: UploadPartsOptions): Promise<void> {
  const reader = o.source.getReader();
  const queue = new ByteQueue();
  const inflight = new Set<Promise<void>>();
  let next = o.firstPart;
  let toSkip = o.skipBytes;
  let failure: unknown = null;

  const schedule = (partNumber: number, bytes: Uint8Array) => {
    const p = putPart(o, partNumber, bytes)
      .then((etag) => o.onPartDone(partNumber, etag, bytes.length))
      .catch((e) => {
        failure ??= e;
      })
      .finally(() => inflight.delete(p));
    inflight.add(p);
  };

  const waitForSlot = async () => {
    while (inflight.size >= o.concurrency) await Promise.race(inflight);
    if (failure) throw failure;
    throwIfAborted(o.signal);
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      queue.push(value);
      if (toSkip > 0) {
        const drop = Math.min(toSkip, queue.length);
        queue.take(drop);
        toSkip -= drop;
        if (toSkip > 0) continue;
      }
      while (queue.length >= o.partSize && next <= o.partCount) {
        await waitForSlot();
        schedule(next++, queue.take(o.partSize));
      }
    }
    if (queue.length > 0 && next <= o.partCount) {
      await waitForSlot();
      schedule(next++, queue.take(queue.length));
    }
    await Promise.all(inflight);
    if (failure) throw failure;
  } catch (e) {
    await reader.cancel().catch(() => undefined);
    throw e;
  }
  if (next !== o.partCount + 1) throw new UploadError(`Scheduled up to part ${next - 1}, expected ${o.partCount}.`);
}

async function putPart(o: UploadPartsOptions, partNumber: number, bytes: Uint8Array): Promise<string> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 4; attempt++) {
    throwIfAborted(o.signal);
    try {
      const url = await o.urlFor(partNumber, attempt > 0);
      const res = await fetch(url, { method: "PUT", body: bytes, signal: o.signal ?? null, mode: "cors" });
      if (res.ok) {
        const etag = res.headers.get("etag");
        if (!etag) {
          throw new UploadError(
            "Storage did not expose the ETag header. Check the bucket CORS policy (ExposeHeaders must include ETag).",
          );
        }
        return etag;
      }
      if (res.status === 403 || res.status >= 500 || res.status === 429) {
        lastError = new UploadError(`Storage responded ${res.status} for part ${partNumber}`);
      } else {
        throw new UploadError(`Storage rejected part ${partNumber} (${res.status})`);
      }
    } catch (e) {
      if (e instanceof UploadError && /ETag|rejected/.test(e.message)) throw e;
      if ((e as Error)?.name === "AbortError") throw e;
      lastError = e;
    }
    await sleep(500 * 2 ** attempt);
  }
  throw new UploadError(`Part ${partNumber} failed after retries`, lastError);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    const e = new Error("Upload cancelled");
    e.name = "AbortError";
    throw e;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
